import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { NgxCurrencyDirective } from 'ngx-currency';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { WizardStepsComponent } from '../../../shared/components/wizard-steps/wizard-steps.component';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { NomeFavoritoModalComponent } from './components/nome-favorito-modal/nome-favorito-modal.component';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { AcaoAjustePrecoEnum } from '../../../shared/enums/produto/acao-ajuste-preco.enum';
import { UnidadeAjustePrecoEnum } from '../../../shared/enums/produto/unidade-ajuste-preco.enum';
import { ExibirCustoEnum } from '../../../shared/enums/produto/exibir-custo.enum';

import { ReajustePrecoService } from '../../../shared/services/apis/produto/reajuste-preco.service';
import { LookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MaskService } from '../../../shared/services/mask.service';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { UtilService } from '../../../shared/services/util.service';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';

import { environment } from '../../../../environments/environment';
import { TipoContatoEnum } from '../../../shared/enums/pessoa/tipo-contato.enum';

import { ListaPrecoPesquisaStruct } from '../../../shared/services/structs/produto/lista-preco-pesquisa.struct';
import { ProdutoReajusteBuscaStruct } from '../../../shared/services/structs/produto/produto-reajuste-busca.struct';
import { LookupItemStruct } from '../../../shared/services/structs/pessoa/lookup-item.struct';
import { TagListItem } from '../../../shared/services/structs/produto/tag-list-item.struct';
import { ReajustePrecoFiltroFavoritoStruct } from '../../../shared/services/structs/produto/reajuste-preco-filtro-favorito.struct';
import { ReajustePrecoBuscarProdutosRequest } from '../../../shared/services/requests/produto/reajuste-preco-buscar-produtos.request';
import { ReajustePrecoRequest } from '../../../shared/services/requests/produto/reajuste-preco.request';

interface ProdutoReajusteLinha extends ProdutoReajusteBuscaStruct {
  selecionadoEtapa2: boolean;
  aplicarNestaLinha: boolean;
  idAcaoAjustePreco: number;
  idUnidadeAjustePreco: number;
  valorAjuste: number;
  novoPreco: number;
  manual: boolean;
}

@Component({
  selector: 'app-reajuste-precos-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTableModule,
    NgxCurrencyDirective,
    AbntPipe,
    HeaderComponent,
    FooterActionComponent,
    WizardStepsComponent,
    AlertCardComponent,
    SelectPesquisavelComponent,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './reajuste-precos-cadastro.component.html',
  styleUrl: './reajuste-precos-cadastro.component.css',
})
export class ReajustePrecosCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_ReajustePreco;

  public acaoEnum = AcaoAjustePrecoEnum;
  public unidadeEnum = UnidadeAjustePrecoEnum;
  public exibirCustoEnum = ExibirCustoEnum;

  public readonly etapas = ['Filtrar produtos', 'Selecionar produtos', 'Informar reajuste'];
  public readonly descricoesEtapas = [
    "Selecione a lista de preços que será ajustada, escolha outros filtros para refinar a busca (opcional) e clique em 'Avançar: Selecionar produtos'.",
    "Marque os produtos que devem receber o reajuste — os que já estão na lista vêm selecionados; marque também os demais se quiser incluí-los na lista com o novo preço — e clique em 'Avançar: Informar reajuste'.",
    "Defina a ação, o tipo de reajuste e o valor. Confira o Novo Preço calculado na tabela e clique em 'Salvar' para concluir.",
  ];
  public readonly stepSubtitles = [
    'Escolha os filtros para localizar os produtos que deseja reajustar.',
    'Marque os produtos que devem receber o reajuste.',
    'Defina o reajuste e confira o novo preço antes de salvar.',
  ];
  public etapaAtual = 0;

  public get stepSubtitle(): string {
    return this.stepSubtitles[this.etapaAtual] ?? '';
  }

  public filtroForm: FormGroup;
  public isLoadingListas = false;
  public isBuscando = false;
  public isSalvando = false;

  public listasPreco: ListaPrecoPesquisaStruct[] = [];
  public listaCategoria: LookupItemStruct[] = [];
  public listaMarca: LookupItemStruct[] = [];
  public listaTag: TagListItem[] = [];
  public urlFornecedor = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Fornecedor}`;

  public favoritos: ReajustePrecoFiltroFavoritoStruct[] = [];
  public favoritoSelecionadoId: number | null = null;

  public linhas: ProdutoReajusteLinha[] = [];

  public painelAcao: number = AcaoAjustePrecoEnum.Somar;
  public painelTipo: number = UnidadeAjustePrecoEnum.Percentual;
  public painelValor: number | null = null;
  public valorMask: any = { ...this.maskService.getPercentMask(), allowNegative: false };

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private reajustePrecoService: ReajustePrecoService,
    private lookupService: LookupService,
    private alertService: AlertService,
    private maskService: MaskService,
    private authService: AuthService
  ) { }

  private get casasDecimaisPreco(): number {
    return this.authService.getUserInfos()?.casasDecimaisPreco || 2;
  }

  ngOnInit(): void {
    this.filtroForm = this.formBuilder.group({
      idListaPreco: [null],
      idCategoria: [null],
      idMarca: [null],
      listIdFornecedor: [[]],
      produto: [''],
      exibirCusto: [ExibirCustoEnum.Nao],
      listIdTag: [[]],
      soPromo: [false],
      exibirImg: [false],
    });

    this.carregarListasPreco();
    this.carregarLookups();
    this.carregarFavoritos();
  }

  // ── Carregamento inicial ─────────────────────────────────────────────────

  private carregarListasPreco(): void {
    this.isLoadingListas = true;
    this.reajustePrecoService.getListasPrecoManual().subscribe({
      next: res => {
        this.isLoadingListas = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.listasPreco = res.listasPreco;
        const padrao = this.listasPreco.find(l => l.isPadrao) ?? this.listasPreco[0];
        if (padrao) {
          this.filtroForm.get('idListaPreco')?.setValue(padrao.idListaPreco);
        }
      },
      error: () => {
        this.isLoadingListas = false;
        this.alertService.show('Erro', 'Não foi possível carregar as listas de preço.', AlertType.error);
      },
    });
  }

  private carregarLookups(): void {
    this.lookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaCategoria = res.listaCategoria || [];
          this.listaMarca = res.listaMarca || [];
          this.listaTag = res.listaTag || [];
        }
      },
    });
  }

  private carregarFavoritos(): void {
    this.reajustePrecoService.getFavoritos().subscribe({
      next: res => {
        if (!res.isError) {
          this.favoritos = res.favoritos;
        }
      },
    });
  }

  // ── Getters de template ──────────────────────────────────────────────────

  public get listaSelecionada(): ListaPrecoPesquisaStruct | undefined {
    return this.listasPreco.find(l => l.idListaPreco === this.filtroForm.get('idListaPreco')?.value);
  }

  public get exibeAvisoListaDiferentePadrao(): boolean {
    const lista = this.listaSelecionada;
    return !!lista && !lista.isPadrao;
  }

  public get hasFavoritoSelecionado(): boolean {
    return this.favoritoSelecionadoId != null;
  }

  public get hasAlgumFiltroPreenchido(): boolean {
    const v = this.filtroForm.value;
    return !!(v.idCategoria || v.idMarca || (v.listIdFornecedor && v.listIdFornecedor.length) || v.produto
      || v.exibirCusto !== ExibirCustoEnum.Nao || (v.listIdTag && v.listIdTag.length) || v.soPromo || v.exibirImg);
  }

  public get exibirBotaoFavoritar(): boolean {
    return this.hasFavoritoSelecionado || this.hasAlgumFiltroPreenchido;
  }

  public get produtosSelecionadosEtapa2(): ProdutoReajusteLinha[] {
    return this.linhas.filter(l => l.selecionadoEtapa2);
  }

  public get qtdSelecionadosEtapa2(): number {
    return this.produtosSelecionadosEtapa2.length;
  }

  public get todosSelecionadosEtapa2(): boolean {
    return this.linhas.length > 0 && this.linhas.every(l => l.selecionadoEtapa2);
  }

  public get qtdForaDaListaEtapa2(): number {
    return this.linhas.filter(l => l.isPrecoBase).length;
  }

  public get linhasParaReajuste(): ProdutoReajusteLinha[] {
    return this.produtosSelecionadosEtapa2;
  }

  public get hasItemComPrecoInvalido(): boolean {
    return this.linhasParaReajuste.some(l => l.novoPreco <= 0);
  }

  public get labelValor(): string {
    return this.painelTipo === this.unidadeEnum.Percentual ? 'Valor do reajuste (%)' : 'Valor do reajuste';
  }

  public get colunasEtapa2(): string[] {
    const colunas = ['select'];
    if (this.filtroForm?.get('exibirImg')?.value) colunas.push('imagem');
    colunas.push('descricao', 'codigo', 'unidade');
    if (this.filtroForm?.get('exibirCusto')?.value !== ExibirCustoEnum.Nao) colunas.push('custo');
    colunas.push('precoAtual');
    return colunas;
  }

  public readonly colunasEtapa3 = ['select', 'descricao', 'codigo', 'unidade', 'precoAtual', 'novoPreco', 'variacao'];

  // ── Favoritos ─────────────────────────────────────────────────────────────

  public selecionarFavorito(idProdutoFiltroFavorito: number | null): void {
    if (idProdutoFiltroFavorito == null) {
      this.favoritoSelecionadoId = null;
      this.filtroForm.reset({
        idListaPreco: this.listasPreco.find(l => l.isPadrao)?.idListaPreco ?? null,
        idCategoria: null,
        idMarca: null,
        listIdFornecedor: [],
        produto: '',
        exibirCusto: ExibirCustoEnum.Nao,
        listIdTag: [],
        soPromo: false,
        exibirImg: false,
      });
      return;
    }

    const favorito = this.favoritos.find(f => f.idProdutoFiltroFavorito === idProdutoFiltroFavorito);
    if (!favorito) return;

    this.favoritoSelecionadoId = favorito.idProdutoFiltroFavorito;
    this.filtroForm.patchValue({
      idListaPreco: favorito.filtros.idListaPreco,
      idCategoria: favorito.filtros.idCategoria ?? null,
      idMarca: favorito.filtros.idMarca ?? null,
      listIdFornecedor: favorito.filtros.listIdFornecedor ?? [],
      produto: favorito.filtros.produto ?? '',
      exibirCusto: favorito.filtros.exibirCusto ?? ExibirCustoEnum.Nao,
      listIdTag: favorito.filtros.listIdTag ?? [],
      soPromo: favorito.filtros.soPromo ?? false,
      exibirImg: favorito.filtros.exibirImg ?? false,
    });
  }

  public abrirFavoritarFiltros(): void {
    const dialogRef = this.dialog.open(NomeFavoritoModalComponent, {
      data: { nomeInicial: `Favorito ${this.favoritos.length + 1}` },
    });

    dialogRef.afterClosed().subscribe((nome: string | undefined) => {
      if (!nome) return;

      this.reajustePrecoService.salvarFavorito({ nome, filtros: this.montarFiltroRequest() }).subscribe({
        next: res => {
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription, AlertType.error);
            return;
          }
          this.carregarFavoritos();
          if (res.favorito) {
            this.favoritoSelecionadoId = res.favorito.idProdutoFiltroFavorito;
          }
        },
        error: () => this.alertService.show('Erro', 'Não foi possível salvar o favorito.', AlertType.error),
      });
    });
  }

  public removerFavoritoSelecionado(): void {
    if (this.favoritoSelecionadoId == null) return;
    const id = this.favoritoSelecionadoId;

    this.reajustePrecoService.removerFavorito(id).subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.favoritoSelecionadoId = null;
        this.carregarFavoritos();
      },
      error: () => this.alertService.show('Erro', 'Não foi possível remover o favorito.', AlertType.error),
    });
  }

  // ── Navegação do wizard ──────────────────────────────────────────────────

  public onEtapaSelecionada(indice: number): void {
    if (indice < this.etapaAtual) {
      this.etapaAtual = indice;
    }
  }

  public get labelBotaoPrimario(): string {
    if (this.etapaAtual === 0) return 'Selecionar produtos';
    if (this.etapaAtual === 1) return 'Informar reajuste';
    return 'Salvar';
  }

  public get labelBotaoSecundario(): string {
    if (this.etapaAtual === 0) return 'Cancelar';
    if (this.etapaAtual === 1) return 'Voltar: Filtrar';
    return 'Cancelar';
  }

  public get botaoPrimarioDesabilitado(): boolean {
    if (this.etapaAtual === 0) return !this.filtroForm.get('idListaPreco')?.value;
    if (this.etapaAtual === 1) return this.qtdSelecionadosEtapa2 === 0;
    return this.hasItemComPrecoInvalido || this.linhasParaReajuste.length === 0;
  }

  public onBotaoPrimario(): void {
    if (this.etapaAtual === 0) {
      this.buscarProdutos();
    } else if (this.etapaAtual === 1) {
      this.irParaEtapaReajuste();
    } else {
      this.abrirConfirmacao();
    }
  }

  public onBotaoSecundario(): void {
    if (this.etapaAtual === 0) {
      this.cancelar();
    } else if (this.etapaAtual === 1) {
      this.etapaAtual = 0;
    } else {
      this.etapaAtual = 1;
    }
  }

  public cancelar(): void {
    this.router.navigate(['/cadastros/reajuste-precos']);
  }

  // ── Etapa 1 → 2: busca de produtos ──────────────────────────────────────

  private montarFiltroRequest(): ReajustePrecoBuscarProdutosRequest {
    const v = this.filtroForm.value;
    return {
      idListaPreco: v.idListaPreco,
      idCategoria: v.idCategoria ?? undefined,
      idMarca: v.idMarca ?? undefined,
      listIdFornecedor: v.listIdFornecedor?.length ? v.listIdFornecedor : undefined,
      produto: v.produto || undefined,
      exibirCusto: v.exibirCusto ?? ExibirCustoEnum.Nao,
      listIdTag: v.listIdTag?.length ? v.listIdTag : undefined,
      soPromo: !!v.soPromo,
      exibirImg: !!v.exibirImg,
    };
  }

  private buscarProdutos(): void {
    if (!this.filtroForm.get('idListaPreco')?.value) {
      this.alertService.show('Atenção', 'Selecione a lista de preços.', AlertType.warning);
      return;
    }

    this.isBuscando = true;
    this.reajustePrecoService.buscarProdutos(this.montarFiltroRequest()).subscribe({
      next: res => {
        this.isBuscando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        this.linhas = res.produtos.map(p => this.criarLinha(p));
        this.etapaAtual = 1;
      },
      error: () => {
        this.isBuscando = false;
        this.alertService.show('Erro', 'Não foi possível buscar os produtos.', AlertType.error);
      },
    });
  }

  private criarLinha(produto: ProdutoReajusteBuscaStruct): ProdutoReajusteLinha {
    return {
      ...produto,
      // Produtos que ainda não pertencem à lista (isPrecoBase) vêm desmarcados por padrão:
      // marcá-los adiciona o produto à lista, então isso deve ser uma escolha deliberada do usuário.
      selecionadoEtapa2: !produto.isPrecoBase,
      aplicarNestaLinha: true,
      idAcaoAjustePreco: this.painelAcao,
      idUnidadeAjustePreco: this.painelTipo,
      valorAjuste: this.painelValor ?? 0,
      novoPreco: produto.precoAtual,
      manual: false,
    };
  }

  public toggleTodosEtapa2(): void {
    const marcar = !this.todosSelecionadosEtapa2;
    this.linhas.forEach(l => (l.selecionadoEtapa2 = marcar));
  }

  public get todosMarcadosEtapa3(): boolean {
    const linhas = this.linhasParaReajuste;
    return linhas.length > 0 && linhas.every(l => l.aplicarNestaLinha);
  }

  public toggleTodosEtapa3(): void {
    const marcar = !this.todosMarcadosEtapa3;
    this.linhasParaReajuste.forEach(l => (l.aplicarNestaLinha = marcar));
  }

  // ── Etapa 2 → 3 ──────────────────────────────────────────────────────────

  private irParaEtapaReajuste(): void {
    this.painelValor = null;
    this.linhasParaReajuste.forEach(l => {
      l.aplicarNestaLinha = true;
      l.novoPreco = l.precoAtual;
      l.manual = false;
    });
    this.etapaAtual = 2;
  }

  // ── Etapa 3: painel de reajuste ──────────────────────────────────────────

  public setAcao(acao: number): void {
    this.painelAcao = acao;
    this.recalcularLinhasAtivas();
  }

  public setTipo(tipo: number): void {
    this.painelTipo = tipo;
    this.valorMask = tipo === this.unidadeEnum.Percentual
      ? { ...this.maskService.getPercentMask(), allowNegative: false }
      : this.getDynamicCurrencyMask();
    this.recalcularLinhasAtivas();
  }

  public getDynamicCurrencyMask() {
    return { ...this.maskService.getDynamicCurrencyMask(), allowNegative: false };
  }

  public onValorChange(): void {
    this.recalcularLinhasAtivas();
  }

  private calcularNovoPreco(precoBase: number, acao: number, unidade: number, valor: number): number {
    const delta = unidade === this.unidadeEnum.Percentual ? precoBase * (valor / 100) : valor;
    const novo = acao === this.acaoEnum.Somar ? precoBase + delta : precoBase - delta;
    return UtilService.arredondarAbnt(novo, this.casasDecimaisPreco);
  }

  private recalcularLinhasAtivas(): void {
    const valor = this.painelValor ?? 0;
    this.linhasParaReajuste
      .filter(l => l.aplicarNestaLinha && !l.manual)
      .forEach(l => {
        l.idAcaoAjustePreco = this.painelAcao;
        l.idUnidadeAjustePreco = this.painelTipo;
        l.valorAjuste = valor;
        l.novoPreco = this.calcularNovoPreco(l.precoAtual, this.painelAcao, this.painelTipo, valor);
      });
  }

  public aplicarAosSelecionados(): void {
    const alvos = this.linhasParaReajuste.filter(l => l.aplicarNestaLinha);
    if (alvos.length === 0) {
      this.alertService.show('Atenção', 'Nenhum produto selecionado', AlertType.warning);
      return;
    }
    if (!this.painelValor) {
      this.alertService.show('Atenção', 'Informe um valor diferente de zero para o reajuste', AlertType.warning);
      return;
    }

    alvos.forEach(l => {
      l.manual = false;
      l.idAcaoAjustePreco = this.painelAcao;
      l.idUnidadeAjustePreco = this.painelTipo;
      l.valorAjuste = this.painelValor as number;
      l.novoPreco = this.calcularNovoPreco(l.precoAtual, this.painelAcao, this.painelTipo, this.painelValor as number);
    });
  }

  public onNovoPrecoManual(linha: ProdutoReajusteLinha): void {
    linha.novoPreco = UtilService.arredondarAbnt(linha.novoPreco, this.casasDecimaisPreco);
    linha.manual = true;
  }

  public variacao(linha: ProdutoReajusteLinha): number {
    if (!linha.precoAtual) return 0;
    return ((linha.novoPreco - linha.precoAtual) / linha.precoAtual) * 100;
  }

  // ── Confirmação e salvar ─────────────────────────────────────────────────

  private abrirConfirmacao(): void {
    const lista = this.listaSelecionada;
    const qtdForaDaLista = this.linhasParaReajuste.filter(l => l.isPrecoBase).length;

    let html = `<div style="display:flex;flex-direction:column;gap:8px;">`
      + `<div><b>Lista de preços:</b> ${lista?.descricao}</div>`
      + `<div><b>Produtos com preço atualizado:</b> ${this.linhasParaReajuste.length}</div>`
      + `</div>`;

    if (qtdForaDaLista > 0) {
      html += `<p style="margin-top:12px;">${qtdForaDaLista} produto(s) serão adicionados à lista '${lista?.descricao}' com o novo preço calculado.</p>`;
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar Reajuste de Preços',
        innerHtml: html,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.salvar();
      }
    });
  }

  private salvar(): void {
    const idListaPreco = this.filtroForm.get('idListaPreco')?.value;
    const request: ReajustePrecoRequest = {
      idListaPreco,
      itens: this.linhasParaReajuste.map(l => ({
        idProduto: l.idProduto,
        precoAplicado: l.novoPreco,
        idAcaoAjustePreco: l.idAcaoAjustePreco,
        idUnidadeAjustePreco: l.idUnidadeAjustePreco,
        valorAjuste: l.valorAjuste,
      })),
    };

    this.isSalvando = true;
    this.reajustePrecoService.salvar(request).subscribe({
      next: res => {
        this.isSalvando = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.alertService.show(
          'Sucesso',
          `Reajuste aplicado com sucesso a ${this.linhasParaReajuste.length} produto(s) na lista '${this.listaSelecionada?.descricao}'.`,
          AlertType.success
        );
        this.router.navigate(['/cadastros/reajuste-precos']);
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Erro', 'Não foi possível aplicar o reajuste.', AlertType.error);
      },
    });
  }
}
